import { getAwardBadgeDisplay } from '@components/app/TrialAwardBadges/AwardBadgeCards'
import { HeaderTimeline } from '@components/primitives/Chart/Timeline/HeaderTimeline'
import { getTimelineValues } from '@components/primitives/Chart/Timeline/getTimelineValues'
import { Icon } from '@components/primitives/Icon'
import { Text } from '@components/primitives/Text'
import { classMerge } from '@components/utilities/classMerge'
import { faListUl } from '@fortawesome/pro-regular-svg-icons/faListUl'
import { faTrophy } from '@fortawesome/pro-regular-svg-icons/faTrophy'
import {
  isMindMedGad3,
  isNeurocrineMdd,
} from '@modules/trials/helpers/isHardcodedTrial'
import type { TrialAssociation } from '@modules/trials/types/TrialAssociation'
import { useRouter } from 'next/navigation'
import type { ListingPageNavbarSections } from '../NavbarTableOfContents'
import { EligibilityHighlights } from './EligibilityHighlights'

export const TrialImagesCarousel = ({ trial }: { trial: TrialAssociation }) => {
  const timeline = getTimelineValues(trial)

  const awardBadges = (trial.awardBadges ?? []).filter(
    (badge) => badge.deletedAt === null,
  )
  const hasAwardBadges = awardBadges.length > 0

  return (
    <div className='relative flex size-full gap-4 overflow-x-auto overflow-y-visible pb-2'>
      <CardWrapper className='min-w-[140%] justify-end md:min-w-[45%]'>
        <EligibilityHighlights trial={trial} />
      </CardWrapper>

      <TimelineCard
        className='min-w-full md:min-w-fit'
        nctId={trial.nctId}
        timeline={timeline}
      />

      {hasAwardBadges &&
        !isMindMedGad3(trial.nctId) &&
        !isNeurocrineMdd(trial.nctId) && (
          <CardWrapper className='min-w-full md:min-w-fit'>
            <AwardsAndHighlightsCard trial={trial} />
          </CardWrapper>
        )}
    </div>
  )
}

const CardWrapper = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => (
  <div
    className={classMerge(
      'flex h-full items-center overflow-hidden rounded-2xl border border-neutral200 bg-neutral50 p-6 pb-0',
      className,
    )}
  >
    {children}
  </div>
)

const TimelineCard = ({
  className,
  nctId,
  timeline,
}: {
  className?: string
  nctId: string
  timeline: ReturnType<typeof getTimelineValues>
}) => {
  const router = useRouter()
  const timelineSection: ListingPageNavbarSections = 'timeline'

  return (
    <div
      className={classMerge(
        'cursor-pointer rounded-2xl border border-neutral200 bg-neutral50 p-6',
        className,
      )}
      onClick={() => {
        router.push(`#${timelineSection}`, undefined)
      }}
    >
      <div className='w-full rounded-2xl bg-white p-4 drop-shadow-xl'>
        <div className='mb-2 flex items-center'>
          <Icon className='mr-2 text-primary500' icon={faListUl} size='1x' />
          <Text styleName='p-small-strong' value='Timeline' />
        </div>
        <HeaderTimeline {...timeline} nctId={nctId} />
      </div>
    </div>
  )
}

export const AwardsAndHighlightsCard = ({
  headerSize = 'p-small-strong',
  trial,
}: {
  headerSize?: 'h5' | 'h4' | 'p-small-strong'
  trial: TrialAssociation
}) => {
  const router = useRouter()
  const awardsSection: ListingPageNavbarSections = 'treatmentDetails'

  return (
    <div
      className='w-full cursor-pointer rounded-2xl bg-white p-3 drop-shadow-xl'
      onClick={() => {
        router.push(`#${awardsSection}`, undefined)
      }}
    >
      <div className='mb-1 flex items-center'>
        <Icon className='mr-2 text-primary500' icon={faTrophy} size='sm' />
        <Text styleName={headerSize} value='Awards & highlights' />
      </div>
      <div className='flex flex-col gap-2 p-1'>
        {(trial.awardBadges ?? [])
          .filter((badge) => badge.deletedAt === null)
          .map((badge) => {
            const { fontColorClassName, icon } = getAwardBadgeDisplay(badge)

            return (
              <div className='flex items-center' key={badge.id}>
                <Icon
                  className={classMerge(
                    'mr-2 inline-block',
                    fontColorClassName,
                  )}
                  icon={icon}
                  size='sm'
                />
                <Text
                  className='text-neutral600'
                  styleName='p-tiny'
                  value={badge.name}
                />
              </div>
            )
          })}
      </div>
    </div>
  )
}
